import React, {useCallback} from 'react';
import {observer} from 'mobx-react';
import s from '../../s';
import {DefaultPCS, renderSettings} from '../../rendering/render.def';
import {EP_INHERITED_LINE_HEIGHT, sanitizeEPContent, transformPtToCqh} from './ep-content-utils';
import ModalSimple from '../../../global/modal-simple';
import * as util from '../../../util';

/**
 * Admin-only engraved-plate template editor (EPMethod.Admin / ep_type=6).
 *
 * The customer-facing Custom editor (ep-editor.tsx) is built on Quill, which
 * normalizes pasted HTML into its own (small) format set. That makes it unable to
 * round-trip the rich template HTML these plates use: it drops <img> logos, custom
 * fonts (anything outside the 10-font whitelist), numeric font-weights (only 700 ->
 * bold survives), letter-spacing and <div> structure, and mangles per-line sizes.
 *
 * This admin view avoids Quill entirely and keeps the source verbatim:
 *   - a <textarea> exposes the raw EPContent HTML for editing / copying,
 *   - a read-only preview renders that HTML at the real plate size so the cqh
 *     font-sizes resolve against --ep-h-pt and the admin sees the actual result.
 *
 * "Copy HTML to clipboard" copies the source exactly so it can be pasted back into
 * the backend Content Text field with zero loss.
 */
export const EPAdminSource = observer(() => {
    const epWidthPx = Math.floor((s.ps.EPWidth || 3) * renderSettings.referenceDPI);
    const epHeightPx = Math.floor((s.ps.EPHeight || 2) * renderSettings.referenceDPI);
    const epHeightPt = Math.round((s.ps.EPHeight || 2) * 72);

    // Preview only: sanitize and convert any pt sizes to cqh so they scale with the
    // plate. The textarea / clipboard still use the untouched s.ps.EPContent source.
    const previewHtml = transformPtToCqh(sanitizeEPContent(s.ps.EPContent || ''));

    const render = useCallback(() => {
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    }, []);

    const applyPlateSize = useCallback(
        (width: number, height: number) => {
            if (!(width > 0 && height > 0)) return;
            DefaultPCS.DefaultEPWidth = width;
            DefaultPCS.DefaultEPHeight = height;
            s.ps.EPWidth = width;
            s.ps.EPHeight = height;
            s.prod.EPWidth = width;
            s.prod.EPHeight = height;
            render();
        },
        [render],
    );

    const onSourceChange = useCallback(
        (value: string) => {
            s.ps.EPContent = value;
            if (s.pcs) s.pcs.EPContent = value;
            render();
        },
        [render],
    );

    const copyHtmlToClipboard = useCallback(async () => {
        const html = s.ps.EPContent || '';
        try {
            await navigator.clipboard.writeText(html);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = html;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
    }, []);

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div style={{fontSize: '0.875rem', lineHeight: 1.5}}>
                <div style={{fontWeight: 600, marginBottom: 4}}>Template HTML (admin)</div>
                <div>Edit the raw plate HTML below. Mark replaceable text with brackets (up to 8 lines), e.g. [line1]&ndash;[line8]. The preview shows it at the plate size; click &ldquo;Copy HTML to clipboard&rdquo; and paste it into the backend Content Text field.</div>
            </div>

            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12}}>
                <label style={{display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: '0.875rem'}}>
                    Width (in)
                    <input
                        type="number"
                        min={0.25}
                        max={24}
                        step={0.125}
                        value={s.ps.EPWidth}
                        onChange={e => applyPlateSize(parseFloat(e.target.value), s.ps.EPHeight)}
                        style={{width: 72, fontSize: '0.875rem', padding: '4px 8px'}}
                        aria-label="Engraved plate width in inches"
                    />
                </label>
                <label style={{display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: '0.875rem'}}>
                    Height (in)
                    <input
                        type="number"
                        min={0.25}
                        max={24}
                        step={0.125}
                        value={s.ps.EPHeight}
                        onChange={e => applyPlateSize(s.ps.EPWidth, parseFloat(e.target.value))}
                        style={{width: 72, fontSize: '0.875rem', padding: '4px 8px'}}
                        aria-label="Engraved plate height in inches"
                    />
                </label>
            </div>

            {/* Live read-only preview, shown at 2x (100% larger) for ep_type=6 so the template
                is easier to design. zoom scales the whole plate uniformly while the inner box
                keeps its true size, so the cqh font-sizes still resolve against --ep-h-pt
                (same approach as EPApprovalPreview). Mirrors the Template-mode preview. */}
            <div style={{display: 'flex', justifyContent: 'center'}}>
                <div style={{display: 'inline-block', zoom: 2}}>
                    <div
                        style={
                            {
                                width: s.ps.css?.ep_width || `${epWidthPx}px`,
                                height: s.ps.css?.ep_height || `${epHeightPx}px`,
                                backgroundImage: s.ps.css?.ep_backgroundImage,
                                backgroundSize: s.ps.css?.ep_backgroundSize,
                                color: s.ps.css?.ep_color,
                                lineHeight: EP_INHERITED_LINE_HEIGHT,
                                containerType: 'size',
                                boxSizing: 'border-box',
                                boxShadow: '1px 1px 4px #000',
                                overflow: 'hidden',
                                '--ep-h-pt': s.ps.css?.ep_plateHeightPt || epHeightPt,
                            } as React.CSSProperties
                        }
                    >
                        <div
                            id="ep-t-div"
                            {...({name: 'ep-t-div'} as any)}
                            style={{
                                padding: s.ps.css?.ep_padding || '0',
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}
                            dangerouslySetInnerHTML={{__html: previewHtml}}
                        />
                    </div>
                </div>
            </div>

            <textarea
                id="ep-t-ta"
                name="ep-t-ta"
                value={s.ps.EPContent || ''}
                onChange={e => onSourceChange(e.target.value)}
                spellCheck={false}
                aria-label="Engraved plate template HTML source"
                style={{width: '100%', minHeight: 200, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre', overflowWrap: 'normal', overflowX: 'auto', boxSizing: 'border-box', padding: 8}}
            />

            <div style={{display: 'flex', justifyContent: 'center'}}>
                <button
                    type="button"
                    className="ep-btn"
                    onClick={() => void copyHtmlToClipboard()}
                    aria-label="Copy engraved plate HTML to clipboard"
                >
                    Copy HTML to clipboard
                </button>
            </div>

            <div style={{display: 'flex', justifyContent: 'center'}}>
                <button
                    type="button"
                    className="ep-btn"
                    onClick={() => util.modal('ep-template-editor-modal')}
                    aria-label="Open the larger plate template editor"
                >
                    Open larger editor
                </button>
            </div>

            <ModalSimple
                id="ep-template-editor-modal"
                title="Plate template editor"
                maxWidth="1100px"
            >
                <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                    {/* Large preview: 100% of the modal width, height auto via aspect-ratio.
                        container-type:size + --ep-h-pt keep the cqh font-sizes correct at any size. */}
                    <div
                        style={
                            {
                                width: '100%',
                                aspectRatio: `${s.ps.EPWidth || 3} / ${s.ps.EPHeight || 2}`,
                                backgroundImage: s.ps.css?.ep_backgroundImage,
                                backgroundSize: s.ps.css?.ep_backgroundSize,
                                color: s.ps.css?.ep_color,
                                lineHeight: EP_INHERITED_LINE_HEIGHT,
                                containerType: 'size',
                                boxSizing: 'border-box',
                                boxShadow: '1px 1px 4px #000',
                                overflow: 'hidden',
                                '--ep-h-pt': s.ps.css?.ep_plateHeightPt || epHeightPt,
                            } as React.CSSProperties
                        }
                    >
                        <div
                            style={{
                                padding: s.ps.css?.ep_padding || '0',
                                width: '100%',
                                height: '100%',
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}
                            dangerouslySetInnerHTML={{__html: previewHtml}}
                        />
                    </div>

                    <textarea
                        value={s.ps.EPContent || ''}
                        onChange={e => onSourceChange(e.target.value)}
                        spellCheck={false}
                        aria-label="Engraved plate template HTML source (large editor)"
                        style={{width: '100%', minHeight: 320, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre', overflowWrap: 'normal', overflowX: 'auto', boxSizing: 'border-box', padding: 8}}
                    />
                </div>
            </ModalSimple>
        </div>
    );
});
